import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import ChevronDwnSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-dwn.svg';
import LoadingIndicator from '@components/Shared/LoadingIndicator/LoadingIndicator';
import Colors from 'pubweb-smokey/dist/colors';
import Button from '@components/Shared/Buttons/Button';

import { FindARetailerStyles } from './FindARetailer.styled';
import { LocationsContext } from '@contexts/LocationsContext';
import { getModelByModelNumber } from '@services/homesService';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import RetailerDealerInfo from './RetailerDealerInfo';

const LocationSelector = dynamic(
  () => import('@components/Shared/LocationSelector/LocationSelector'),
  { ssr: false }
);

const NUMBER_DEALERS_VISIBLE_DETAILS = 3;
const NUMBER_DEALERS_VISIBLE_DETAILS_INCREMENT = 3;

const FindARetailer = ({ modelNumber }) => {
  const locationsContext = useContext(LocationsContext);

  const [numDealersVisible, setNumDealersVisible] = useState(
    NUMBER_DEALERS_VISIBLE_DETAILS
  );
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [nearbyDealers, setNearbyDealers] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const sortDistance = (dealers) => {
    dealers.sort((a, b) => {
      return a.distanceToDealer - b.distanceToDealer;
    });
    return dealers;
  };

  const updateDealerList = () => {
    if (
      locationsContext &&
      locationsContext.state &&
      locationsContext.state.geocode
    ) {
      getModelByModelNumber(
        modelNumber,
        locationsContext.state.geocode.latitude,
        locationsContext.state.geocode.longitude,
        locationsContext.state.distance
      ).then((result) => {
        if (result !== null && result.dealers !== null) {
          let { dealers } = result;

          let inStockDealers = dealers.filter(
            (dealer) => dealer.hasModelInStock
          );
          let remainingDealers = dealers.filter(
            (dealer) => !dealer.hasModelInStock
          );

          inStockDealers = sortDistance(inStockDealers);
          remainingDealers = sortDistance(remainingDealers);

          dealers = [...inStockDealers, ...remainingDealers];

          setNearbyDealers(dealers);
        } else {
          setNearbyDealers([]);
        }

        setNumDealersVisible(NUMBER_DEALERS_VISIBLE_DETAILS);
        locationsContext.actions.setUpdating(false);
        setUpdating(false);
      });
    }
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    setUpdating(true);
    updateDealerList();
  }, [locationsContext.state.geocode, locationsContext.state.distance]);

  const dealerCards =
    nearbyDealers &&
    nearbyDealers.map((dealer, i) => {
      return (
        <>
          <RetailerDealerInfo
            key={'dealer-info-' + (i + 1)}
            pinNumber={i + 1}
            dealerNumber={dealer.dealerNumber}
            inStock={dealer.hasModelInStock}
            distance={dealer.distanceToDealer}
            name={dealer.dealerName}
            address1={dealer.address1}
            address2={dealer.address2}
            city={dealer.city}
            state={dealer.stateProvince}
            postalCode={dealer.postalCode}
            phone={dealer.phoneNumber}
            slug={dealer.navigationSlug}
          />
        </>
      );
    });

  return (
    <FindARetailerStyles
      $overlay={updating}
      $noLocations={
        !updating && nearbyDealers !== null && nearbyDealers.length === 0
      }
    >
      <h3>Find A Retailer</h3>
      <>
        {isClient && (
          <p className="purchase-location intro" id="purchase-loc">
            Purchase this home within
            <a
              href="#"
              onClick={(ev) => {
                ev.preventDefault();
                setShowLocationSelector(true);
                // If the location selector dialog was opened too close to the bottom, it was not able to be fully displayed.
                const el = document.getElementById('purchase-loc');
                if (el && typeof window !== 'undefined') {
                  if (
                    el.getBoundingClientRect().bottom + 350 >
                    window.innerHeight
                  ) {
                    window.scrollTo({ top: window.pageYOffset + 350 });
                  }
                }
              }}
            >
              &nbsp;{locationsContext.state.distance} miles of{' '}
              {locationsContext.state.postalCode} <ChevronDwnSvg />
            </a>
          </p>
        )}
        {showLocationSelector && (
          <LocationSelector
            applyButtonClassName="gtm-update-zip-dealers"
            locationKey="dealers"
            postalCode={locationsContext.state.postalCode}
            distance={locationsContext.state.distance}
            onUpdate={locationsContext.actions.updateLocation}
            setUpdating={locationsContext.actions.setUpdating}
            onCloseClick={() => {
              setShowLocationSelector(false);
            }}
          />
        )}
        <div className="retailer-list">
          {updating ? (
            <div className="loading-indicator">
              <LoadingIndicator
                color={Colors.primary.claytonBlue.standard}
                width={64}
                height={64}
              />
            </div>
          ) : !nearbyDealers !== null ? (
            nearbyDealers !== null && nearbyDealers.length > 0 ? (
              dealerCards.slice(0, numDealersVisible)
            ) : (
              <div className="no-retailers">
                <h3>Home unavailable within search area.</h3>
                <p>
                  Please select a different location from the filter above or
                  view similar homes.
                </p>
                <Button
                  className="no-retailers-button"
                  automationId="no-retailers-found"
                  fillContainer={true}
                  nextJSUrl="/find-a-home/"
                  linkComponent={Link}
                >
                  View Similar Homes
                </Button>
              </div>
            )
          ) : null}
        </div>
        <div>
          {!updating &&
            nearbyDealers !== null &&
            numDealersVisible < nearbyDealers.length && (
              <Button
                className="load-more"
                automationId="locations-load-more"
                onClick={(e) => {
                  e.target.blur();
                  pushGTMEvent('loadMoreDealersClick');
                  setNumDealersVisible(
                    numDealersVisible + NUMBER_DEALERS_VISIBLE_DETAILS_INCREMENT
                  );
                }}
              >
                Load More
              </Button>
            )}
        </div>
      </>
    </FindARetailerStyles>
  );
};

FindARetailer.propTypes = {
  modelNumber: PropTypes.string,
};

export default FindARetailer;
